import { IGfpaDetailsDTO } from '@/interfaces/gfpaReport.interface'
import { IGfrDetailsDTO } from '@/interfaces/gfrReport.interface'
import {
  IDetailsChartDrilldownData,
  IDetailsChartSeriesData,
  IDetailsDataMap,
  IRvSecurityDetailsData,
} from '@/interfaces/rvDetails.interface'

export const transformChartData = (
  responseData: IGfpaDetailsDTO[] | IRvSecurityDetailsData[] | IGfrDetailsDTO[],
  groupingKeys: string[],
  valueKey: string,
  labelName: string,
): IDetailsDataMap => {
  // Recursive function to group data and generate drilldown data
  const groupData = (
    data: IGfpaDetailsDTO[] | IRvSecurityDetailsData[] | IGfrDetailsDTO[],
    groupKeys: string[],
    level: number = 0,
    idMap: { [key: string]: number } = {},
  ): {
    series: IDetailsChartSeriesData[]
    drilldown: IDetailsChartDrilldownData[]
  } => {
    const currentGroupKey = groupKeys[level]
    const groupedData = data.reduce(
      (
        acc: {
          [key: string]: {
            contributionSum: number
            details:
              | IGfpaDetailsDTO[]
              | IRvSecurityDetailsData[]
              | IGfrDetailsDTO[]
          }
        },
        item:
          | IGfpaDetailsDTO
          | IRvSecurityDetailsData
          | IGfrDetailsDTO
          | { [key: string]: string },
      ) => {
        const groupKey = (item as { [key: string]: string })[currentGroupKey]
        if (groupKey !== '') {
          if (!acc[groupKey]) {
            acc[groupKey] = { contributionSum: 0, details: [] }
          }
          acc[groupKey].contributionSum += (item as any)[valueKey]
          acc[groupKey].details.push(item as any)
        }
        return acc
      },
      {},
    )

    const totalContributionSum = Object.values(groupedData).reduce(
      (acc, group) => acc + group.contributionSum,
      0,
    )

    const seriesData = []
    const drilldownData = []

    for (const [group, data] of Object.entries(groupedData)) {
      const percentContribution =
        (data.contributionSum / totalContributionSum) * 100
      // Create the drilldownId based on all the relevant grouping keys
      const itemDetail = data.details[0] as
        | IGfpaDetailsDTO
        | IRvSecurityDetailsData
        | IGfrDetailsDTO
      const groupingKey = groupingKeys[0] as keyof (
        | IGfpaDetailsDTO
        | IRvSecurityDetailsData
        | IGfrDetailsDTO
      )
      const drilldownBaseId = `${itemDetail[groupingKey]}_${group}`
      idMap[drilldownBaseId] = (idMap[drilldownBaseId] || 0) + 1
      const drilldownId =
        idMap[drilldownBaseId] > 1
          ? `${drilldownBaseId}_${idMap[drilldownBaseId]}`
          : drilldownBaseId
      // Use sectornm for display name, fallback to sector, then group key
      const firstItem = data.details[0] as any
      const displayName = firstItem?.sectornm || firstItem?.sector || group
      
      seriesData.push({
        name: displayName,
        y: data.contributionSum,
        labelToDisplay: displayName,
        drilldown: drilldownId,
        percentContribution: percentContribution.toFixed(2),
      })

      const nextLevel = level + 1
      if (nextLevel < groupKeys.length) {
        const drilldownDetails = groupData(
          data.details,
          groupKeys,
          nextLevel,
          idMap,
        )
        drilldownData.push({
          id: drilldownId,
          name: displayName,
          data: drilldownDetails.series,
        })
        drilldownData.push(...drilldownDetails.drilldown)
      } else {
        const drilldownDetails = data.details.map((item: any) => {
          const percentContribution =
            (item[valueKey] / data.contributionSum) * 100
          return {
            name: labelName,
            y: item[valueKey],
            labelToDisplay: item.securityId || item.security_id,
            otherDetails: item,
            percentContribution: percentContribution.toFixed(2),
          }
        })
        drilldownData.push({
          id: drilldownId,
          name: displayName,
          data: drilldownDetails,
        })
      }
    }

    return { series: seriesData, drilldown: drilldownData }
  }

  const { series, drilldown } = groupingKeys.length
    ? groupData(responseData, groupingKeys)
    : { series: [], drilldown: [] }

  const detailsDataMap = {
    seriesData: series,
    drilldownData: drilldown,
  }
  return detailsDataMap
}
